import { useRef } from 'react'
import { Upload } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui'
import { useImportTemplate } from '../api/queries'

/**
 * Toolbar button that opens a hidden file input, uploads the selected
 * `.xlsx` to `POST /api/formulations/import-template`, and surfaces the
 * created-or-updated formulation via a toast.
 */

export interface ImportTemplateButtonProps {
  onImported?: (formulationId: number) => void
}

export function ImportTemplateButton({ onImported }: ImportTemplateButtonProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const mut = useImportTemplate()

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    // Reset the input so re-selecting the same file fires a new change event.
    e.target.value = ''
    try {
      const res = await mut.mutateAsync(file)
      toast.success(
        `${res.created ? 'Imported' : 'Updated'} ${res.formulation.formula_code} — ${res.formulation.name}`,
      )
      onImported?.(res.formulation.id)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Import failed'
      toast.error(`Import failed: ${msg}`)
    }
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        onChange={handleChange}
        className="sr-only"
      />
      <Button
        variant="secondary"
        size="sm"
        onClick={() => inputRef.current?.click()}
        loading={mut.isPending}
        title="Import an existing formulation from an Excel template (.xlsx)"
      >
        <Upload size={14} />
        Import template
      </Button>
    </>
  )
}
